import DataSource from "devextreme/data/data_source";
import CustomStore from "devextreme/data/custom_store";
import { defer, forkJoin, iif, lastValueFrom, of } from "rxjs";
import { LoadOptions } from "devextreme/data";
import { catchError, map } from "rxjs/operators";
import { TagLight } from "src/app/backend/models/tag/tag";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { TagBackend } from "src/app/backend/api/tag/tag.backend";
import { dxGetFilters, getSelectParamByLoadOptions } from "src/app/support/dx";
import { compact } from "es-toolkit";

import { sntLog } from "src/app/common/logger";

const logger = sntLog("TagsDatasource");
type Providers = {
  tagsBackend: TagBackend;
};

type Options = {
  disableCreateTag: boolean;
  category: string;
  getTenant?: () => string | undefined;
};

export function buildTagsDataSource(
  { tagsBackend }: Providers,
  options: Options
) {
  return new DataSource({
    paginate: true,
    store: new CustomStore({
      insert: async (insertOptions) => {
        return await lastValueFrom(
          tagsBackend.create({
            name: insertOptions.name,
            color: insertOptions.color,
            category: options?.category
          })
        );
      },
      load: async (loadOptions: LoadOptions) => {
        const tenant = options.getTenant?.();
        const loadOptionsFilters = dxGetFilters(loadOptions);
        const idsFromFilters = loadOptionsFilters?.id
          ? loadOptionsFilters.id.split(",")
          : [];
        const { page, pageSize } = getSelectParamByLoadOptions(loadOptions);

        return await lastValueFrom(
          forkJoin({
            tagsByIds: iif(
              () => idsFromFilters.length > 0,
              defer(() =>
                forkJoin(
                  idsFromFilters.map((id) =>
                    tagsBackend.loadLight(id).pipe(
                      map((tag) => (tag.deleted ? undefined : tag)),
                      catchError((error) => {
                        logger.error("Error loading light tag: ", error);
                        return of(undefined);
                      })
                    )
                  )
                )
              ),
              of([])
            ),
            tags: iif(
              () => idsFromFilters.length === 0,
              defer(() => {
                return tagsBackend
                  .search(loadOptions.searchValue, options!.category, {
                    page,
                    pageSize,
                    tenant
                  })
                  .pipe(
                    map((tags: TagLight[]) => {
                      if (
                        !options?.disableCreateTag &&
                        (!tags || tags.length === 0)
                      ) {
                        return [
                          {
                            id: "-1",
                            disabled: true,
                            name: sntTranslate(
                              marker("Premi invio per creare un nuovo tag")
                            )
                          }
                        ];
                      }

                      if (!!options?.disableCreateTag) {
                        return tags;
                      }

                      return compact([
                        page === 1
                          ? {
                              id: "-1",
                              disabled: true,
                              name: sntTranslate(
                                marker(
                                  "Se il tag non è nei risultati proposti premi INVIO per creare uno nuovo"
                                )
                              )
                            }
                          : null,
                        ...tags
                      ]);
                    })
                  );
              }),
              of([])
            )
          }).pipe(
            map(({ tagsByIds, tags }) => {
              return [...tagsByIds.filter((tag) => !!tag), ...tags];
            })
          )
        );
      }
    })
  });
}
